import './sentry';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { registerSW } from 'virtual:pwa-register';

import * as Sentry from '@sentry/react';

// react dnd
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

// project imports
import App from './App';
import { store, persistor } from './store';

// style + assets
import 'assets/scss/style.scss';

const PRELOAD_RECOVERY_KEY = '__vite_preload_recovery_ts__';
const PRELOAD_RECOVERY_PARAM = '__reload';
const PRELOAD_RECOVERY_WINDOW_MS = 30 * 1000;

const getLastPreloadRecoveryAt = () => {
  const value = Number(sessionStorage.getItem(PRELOAD_RECOVERY_KEY));
  return Number.isFinite(value) ? value : null;
};

const cleanupPreloadRecoveryParam = () => {
  const url = new URL(window.location.href);
  if (!url.searchParams.has(PRELOAD_RECOVERY_PARAM)) {
    return;
  }

  // Keep URLs clean after a successful recovery reload.
  url.searchParams.delete(PRELOAD_RECOVERY_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
};

// Recover from stale chunk/CSS preload failures (typically during deployment transitions).
window.addEventListener('vite:preloadError', (event) => {
  // Stop Vite from re-throwing this as an uncaught runtime error.
  event.preventDefault();

  const now = Date.now();
  const lastRecoveryAt = getLastPreloadRecoveryAt();

  // Limit auto-reload attempts to avoid loops when a recovery navigation cannot resolve the issue.
  if (lastRecoveryAt && now - lastRecoveryAt < PRELOAD_RECOVERY_WINDOW_MS) {
    console.error('Vite preload recovery recently attempted; manual refresh may be required.', event.payload);
    return;
  }

  // Mark recovery as attempted before navigating away.
  sessionStorage.setItem(PRELOAD_RECOVERY_KEY, String(now));

  // Clone current URL so we can append a cache-busting query param.
  const url = new URL(window.location.href);

  // Force a distinct navigation URL, helping bypass stale HTML/asset mappings.
  url.searchParams.set(PRELOAD_RECOVERY_PARAM, String(now));

  // Replace current history entry and reload into the latest deployment assets.
  window.location.replace(url.toString());
});

cleanupPreloadRecoveryParam();

registerSW({
  onNeedRefresh() {
    console.debug('New content is available; please refresh.');
  },
  onOfflineReady() {
    console.debug('App is ready to work offline.');
  },
  onRegisteredSW(_scriptUrl, registration) {
    if (!registration) {
      return;
    }

    const intervalId = window.setInterval(
      () => {
        if (document.visibilityState !== 'visible' || !navigator.onLine) {
          return;
        }

        console.debug('Checking for service worker updates...');
        void registration.update().catch((error) => {
          console.error('Service worker update check failed:', error);
        });
      },
      5 * 60 * 1000
    );

    window.addEventListener(
      'beforeunload',
      () => {
        window.clearInterval(intervalId);
      },
      { once: true }
    );
  },
  onRegisterError(error) {
    console.error('Error during service worker registration:', error);
  }
});

// ==============================|| REACT DOM RENDER  ||============================== //

// const root = ReactDOM.createRoot(document.getElementById('root'));

const container = document.getElementById('root');
const root = createRoot(container, {
  // Callback called when an error is thrown and not caught by an ErrorBoundary.
  onUncaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  // Callback called when React catches an error in an ErrorBoundary.
  onCaughtError: Sentry.reactErrorHandler((error, errorInfo) => {
    console.warn('Uncaught error', error, errorInfo.componentStack);
  }),
  // Callback called when React automatically recovers from errors.
  onRecoverableError: Sentry.reactErrorHandler()
});

root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <BrowserRouter>
        <DndProvider backend={HTML5Backend}>
          <App />
        </DndProvider>
      </BrowserRouter>
    </PersistGate>
  </Provider>
);
